import { useState, useEffect } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ImageLightbox - A modal component for viewing images in full size
 * @param {string} src - The image source URL
 * @param {string} alt - Alt text for the image
 * @param {string[]} images - Optional array of images for gallery mode
 * @param {number} initialIndex - Initial image index when in gallery mode
 * @param {function} onClose - Callback when modal is closed
 * @param {boolean} isOpen - Whether the modal is open
 */
export const ImageLightbox = ({ src, alt, images = [], initialIndex = 0, onClose, isOpen }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  // Use images array if provided, otherwise use single image
  const imageList = images.length > 0 ? images : (src ? [src] : []);
  const hasMultipleImages = imageList.length > 1;
  
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Reset to initial index when opening
      setCurrentIndex(initialIndex);
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose?.();
          break;
        case 'ArrowLeft':
          if (hasMultipleImages) {
            setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageList.length - 1));
          }
          break;
        case 'ArrowRight':
          if (hasMultipleImages) {
            setCurrentIndex((prev) => (prev < imageList.length - 1 ? prev + 1 : 0));
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasMultipleImages, imageList.length, onClose]);
  
  if (!isOpen || imageList.length === 0) return null;
  
  const currentImage = imageList[currentIndex];
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        data-testid="lightbox-close-btn"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Navigation arrows */}
      {hasMultipleImages && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageList.length - 1));
            }}
            className="absolute left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((prev) => (prev < imageList.length - 1 ? prev + 1 : 0));
            }}
            className="absolute right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}
      
      {/* Image container */}
      <div 
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImage}
          alt={alt || 'Enlarged image'}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
      </div>
      
      {/* Image counter */}
      {hasMultipleImages && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm">
          {currentIndex + 1} / {imageList.length}
        </div>
      )}
    </div>
  );
};

/**
 * EnlargeableImage - Wrapper component that adds click-to-enlarge functionality
 */
export const EnlargeableImage = ({ 
  src, 
  alt, 
  className = "", 
  images = [],
  initialIndex = 0,
  showZoomIcon = true,
  onError,
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <div className="relative group cursor-pointer" onClick={() => setIsOpen(true)}>
        <img
          src={src}
          alt={alt}
          className={className}
          onError={onError}
          {...props}
        />
        {showZoomIcon && (
          <div className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <ZoomIn className="w-5 h-5" />
          </div>
        )}
      </div>
      <ImageLightbox
        src={src}
        alt={alt}
        images={images}
        initialIndex={initialIndex}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export default ImageLightbox;
